import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { CheckCircle, AlertTriangle, XCircle, Loader2 } from "lucide-react";

interface ValidationIssue {
  id: string;
  icon: string;
  label: string;
  status: 'success' | 'warning' | 'error';
  message: string;
  count?: number;
  actionLabel?: string;
  actionPath?: string;
}

export const PreExportValidation = () => {
  const chartRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const [issues, setIssues] = useState<ValidationIssue[]>([]);

  // Real-time validation checks
  const { data: validationData, isLoading } = useQuery({
    queryKey: ['pre-export-validation'],
    queryFn: async () => {
      // Get all transactions for validation
      const { data: transactions, error } = await supabase
        .from('transactions')
        .select('*')
        .order('transaction_date', { ascending: false });

      if (error) throw error;

      // Calculate validation metrics
      const totalCount = transactions?.length || 0;
      const unclassified = transactions?.filter(t => !t.account_number || t.account_number === '').length || 0;
      const missingVat = 0; // VAT validation removed - not tracked in transaction type
      const needsReview = transactions?.filter(t => t.needs_review).length || 0;

      // Balance check
      let totalDebit = 0;
      let totalCredit = 0;
      transactions?.forEach(t => {
        const amount = Math.abs(parseFloat(t.amount?.toString() || '0'));
        if (parseFloat(t.amount?.toString() || '0') < 0) {
          totalDebit += amount;
        } else {
          totalCredit += amount;
        }
      });
      const balanceDiff = Math.abs(totalDebit - totalCredit);
      const isBalanced = balanceDiff < 0.01;

      return {
        totalCount,
        unclassified,
        missingVat,
        needsReview,
        isBalanced,
        balanceDiff,
        classificationRate: totalCount > 0 ? ((totalCount - unclassified) / totalCount * 100) : 100
      };
    },
    refetchInterval: 30000, // Refresh every 30s
  });

  // Calculate overall score and issues
  useEffect(() => {
    if (!validationData) return;

    const newIssues: ValidationIssue[] = [];
    let score = 100;

    // Check 1: Classification Status
    if (validationData.unclassified > 0) {
      score -= 25;
      newIssues.push({
        id: 'classification',
        icon: 'fa-tags',
        label: 'Classification Missing',
        status: validationData.unclassified > 10 ? 'error' : 'warning',
        message: `${validationData.unclassified} transactions need account assignment`,
        count: validationData.unclassified,
        actionLabel: 'Classify Now',
        actionPath: '/transactions'
      });
    } else {
      newIssues.push({
        id: 'classification',
        icon: 'fa-tags',
        label: 'Classification Complete',
        status: 'success',
        message: 'All transactions classified'
      });
    }

    // Check 2: VAT Completeness
    if (validationData.missingVat > 0) {
      score -= 20;
      newIssues.push({
        id: 'vat',
        icon: 'fa-percent',
        label: 'VAT Information Missing',
        status: 'warning',
        message: `${validationData.missingVat} transactions missing VAT rates`,
        count: validationData.missingVat,
        actionLabel: 'Add VAT',
        actionPath: '/transactions'
      });
    } else {
      newIssues.push({
        id: 'vat',
        icon: 'fa-percent',
        label: 'VAT Complete',
        status: 'success',
        message: 'All VAT rates assigned'
      });
    }

    // Check 3: Balance Verification
    if (!validationData.isBalanced) {
      score -= 30;
      newIssues.push({
        id: 'balance',
        icon: 'fa-scale-balanced',
        label: 'Balance Mismatch',
        status: 'error',
        message: `Debit/Credit difference: CHF ${validationData.balanceDiff.toFixed(2)}`,
        actionLabel: 'Review Transactions',
        actionPath: '/transactions'
      });
    } else {
      newIssues.push({
        id: 'balance',
        icon: 'fa-scale-balanced',
        label: 'Balance Verified',
        status: 'success',
        message: 'Debit and Credit balanced'
      });
    }

    // Check 4: Validation Queue
    if (validationData.needsReview > 0) {
      score -= 15;
      newIssues.push({
        id: 'review',
        icon: 'fa-flag-checkered',
        label: 'Pending Validation',
        status: 'warning',
        message: `${validationData.needsReview} transactions need review`,
        count: validationData.needsReview,
        actionLabel: 'Validate',
        actionPath: '/validation'
      });
    } else {
      newIssues.push({
        id: 'review',
        icon: 'fa-flag-checkered',
        label: 'All Validated',
        status: 'success',
        message: 'No pending validations'
      });
    }

    setIssues(newIssues);

    // Update Highcharts gauge
    if (chartRef.current && typeof window !== 'undefined' && window.Highcharts) {
      const finalScore = Math.max(0, score);
      window.Highcharts.chart(chartRef.current, {
        chart: { type: 'solidgauge', backgroundColor: 'transparent' },
        title: null,
        pane: {
          center: ['50%', '85%'],
          size: '140%',
          startAngle: -90,
          endAngle: 90,
          background: {
            backgroundColor: '#ecf4e9',
            innerRadius: '60%',
            outerRadius: '100%',
            shape: 'arc',
            borderWidth: 0
          }
        },
        tooltip: { enabled: false },
        yAxis: {
          min: 0,
          max: 100,
          stops: [
            [0.1, '#f63440'],
            [0.5, '#f59e0b'],
            [0.9, '#1e4841']
          ],
          lineWidth: 0,
          tickWidth: 0,
          minorTickInterval: null,
          tickAmount: 2,
          labels: { enabled: false }
        },
        plotOptions: {
          solidgauge: {
            dataLabels: {
              y: 5,
              borderWidth: 0,
              useHTML: true,
              format: '<div style="text-align:center"><span style="font-size:2.5rem; color:#1e4841; font-weight:bold; font-family: \'Space Grotesk\', sans-serif;">{y}%</span></div>',
            },
            innerRadius: '60%'
          }
        },
        series: [{ name: 'Readiness', data: [finalScore] }],
        credits: { enabled: false },
      });
    }
  }, [validationData]);

  const errorCount = issues.filter(i => i.status === 'error').length;
  const warningCount = issues.filter(i => i.status === 'warning').length;
  const overallStatus = errorCount > 0 ? 'error' : warningCount > 0 ? 'warning' : 'success';

  const getStatusBadge = () => {
    if (isLoading) return { label: 'Checking...', className: 'bg-gray-200 text-gray-700' };
    if (overallStatus === 'error') return { label: 'Not Ready', className: 'bg-red-100 text-red-700' };
    if (overallStatus === 'warning') return { label: 'Review Needed', className: 'bg-orange-100 text-orange-700' };
    return { label: 'Ready', className: 'bg-green-100 text-green-700' };
  };

  const statusBadge = getStatusBadge();

  return (
    <div className="bg-white p-6 rounded-2xl border border-brand-border">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-bold text-brand-primary">Pre-Export Validation</h3>
        <div className={`px-2.5 py-1 rounded-2xl text-xs font-medium ${statusBadge.className}`}>
          {statusBadge.label}
        </div>
      </div>
      
      <div className="flex flex-col items-center mb-4">
        <div ref={chartRef} className="w-full h-[160px]"></div>
        <p className="text-sm text-brand-text-secondary -mt-6">Export Readiness Score</p>
      </div>
      
      {isLoading ? (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin text-brand-primary" />
        </div>
      ) : (
        <div className="space-y-2">
          {issues.map(issue => (
            <div
              key={issue.id}
              className={`flex items-center justify-between p-3 rounded-lg ${
                issue.status === 'success' ? 'bg-brand-secondary' :
                issue.status === 'warning' ? 'bg-orange-50 border border-orange-200' :
                'bg-red-50 border border-red-200'
              }`}
            >
              <div className="flex items-center gap-3 flex-1">
                <i className={`fa-solid ${issue.icon} ${
                  issue.status === 'success' ? 'text-green-600' :
                  issue.status === 'warning' ? 'text-orange-600' :
                  'text-red-600'
                }`}></i>
                <div className="flex-1">
                  <span className={`text-sm font-medium ${
                    issue.status === 'error' ? 'text-red-700' :
                    issue.status === 'warning' ? 'text-orange-700' :
                    'text-brand-text-primary'
                  }`}>
                    {issue.label}
                  </span>
                  <p className="text-xs text-brand-text-secondary">{issue.message}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {issue.count !== undefined && issue.count > 0 && (
                  <span className="px-2 py-0.5 bg-white rounded text-xs font-medium">
                    {issue.count}
                  </span>
                )}
                {issue.status === 'success' ? (
                  <CheckCircle className="h-5 w-5 text-green-600" />
                ) : issue.actionLabel && issue.actionPath ? (
                  <button
                    onClick={() => navigate(issue.actionPath!)}
                    className="px-3 py-1 bg-brand-primary text-white rounded-lg text-xs font-medium hover:bg-brand-primary/90 transition-colors"
                  >
                    {issue.actionLabel}
                  </button>
                ) : issue.status === 'warning' ? (
                  <AlertTriangle className="h-5 w-5 text-orange-600" />
                ) : (
                  <XCircle className="h-5 w-5 text-red-600" />
                )}
              </div>
            </div>
          ))}
        </div>
      )}
      
      {!isLoading && (errorCount > 0 || warningCount > 0) && (
        <p className="text-xs text-brand-text-secondary text-center mt-4">
          {errorCount > 0 && `${errorCount} critical issue${errorCount > 1 ? 's' : ''}`}
          {errorCount > 0 && warningCount > 0 && ', '}
          {warningCount > 0 && `${warningCount} warning${warningCount > 1 ? 's' : ''}`} found.
        </p>
      )}
    </div>
  );
};